import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_URL from '../api';
import './landing.css';

import { PLACEHOLDER_METRICS } from './data/content';
import { refreshScroll } from './hooks/scrollEngine';

import Navbar from './sections/Navbar';
import Hero from './sections/Hero';
import TalentStory from './sections/TalentStory';
import HowItWorks from './sections/HowItWorks';
import OpportunityShowcase from './sections/OpportunityShowcase';
import StudentSection from './sections/StudentSection';
import CompanySection from './sections/CompanySection';
import Metrics from './sections/Metrics';
import FinalCTA from './sections/FinalCTA';
import Footer from './sections/Footer';

/* ==================================================================
   LandingPage
   ------------------------------------------------------------------
   Composition root. It owns exactly two pieces of state:

   - `entered`, which fades the page in once mounted (the page
     transition), and
   - `live`, the real platform counts from the public /api/stats.

   Everything else is either static content from data/content.js or
   CSS-variable motion driven by the shared scroll engine.
   ================================================================== */

export default function LandingPage() {
  const [entered, setEntered] = useState(false);
  const [live, setLive] = useState(null);

  /* Fade the page in on the frame after mount. */
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  /* Real counts where we have them; placeholders stay flagged. */
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/api/stats`)
      .then((res) => {
        if (alive) setLive(res.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /* A deep link like /#opportunities arrives before this page has
     rendered, so the browser's own hash scroll finds nothing. Do it once
     the sections exist. */
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return undefined;
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  /* Webfonts and late images shift the layout; one recompute after
     load keeps every scroll-driven section honest. */
  useEffect(() => {
    if (document.readyState === 'complete') {
      refreshScroll();
      return undefined;
    }
    window.addEventListener('load', refreshScroll);
    return () => window.removeEventListener('load', refreshScroll);
  }, []);

  const metrics = useMemo(() => {
    if (!live) return PLACEHOLDER_METRICS;
    return PLACEHOLDER_METRICS.map((metric) => {
      if (metric.source !== 'live') return metric;
      const real = metric.id === 'students' ? live.students : live.recruiters;
      if (!real) return metric;
      return { ...metric, value: real, suffix: '', placeholder: false };
    });
  }, [live]);

  return (
    <div className={`hs ${entered ? 'is-entered' : ''}`}>
      <Navbar />
      <main>
        <Hero />
        <TalentStory />
        <HowItWorks />
        <OpportunityShowcase />
        <StudentSection />
        <CompanySection />
        <Metrics metrics={metrics} />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
